import { Component, inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule } from "@angular/forms";
import { BaseComponent } from "src/ancestors/base/base.component";
import { UrlCollection } from "../../../constants/url-collection";
import { BackofficeProcessPhases, MonitoringSummary, MonitoringSummaryDevelopmentPlan, MonitoringUserFiltersValues, MonitoringUserSortEnumTypes, ProcessStatusEnum, ProcessTypeEnum, ProcessTypeEnumTypes, TalentMatrixUser } from "alloy-performance-engine-cl";
import { RouterModule } from "@angular/router";
import { AlertService } from "../../../services/alert.service";
import { TranslateService } from "../../../util/translate.service";
import { MenuItem } from "primeng/api";
import { Subject } from "rxjs";
import { MonitoringBackofficeApiService, MonitoringUserDevelopmentPlanSummaryExtended, MonitoringUserSummaryExtended, MonitoringUserSummaryIdsExtended, ProcessDetailsFilters } from "src/app/services/api/monitoring-backoffice-api.service";
import { StatusCardData } from "src/app/components/ape-status-card/ape-status-card.component";
import { TRANSLATION_KEYS } from "src/app/constants/translations/translation-keys";
import { ApeMonitoringDetailsTemplateComponent } from "src/app/templates/process-monitoring-details-template/process-monitoring-details-template.component";
import { ApeToastComponent } from "src/app/components/ape-toast/ape-toast.component";
import { ImpersonationService } from "../../../services/impersonation.service";
import { MonitoringCalibrationBackofficeApiService } from "src/app/services/api/monitoring-calibration-backoffice-api.service";
import { PersonSummary } from "src/app/components/ape-calibration-talent-matrix-confirmation-modal/ape-calibration-talent-matrix-confirmation-modal.component";
import { ApeDownloadButtonService } from "src/app/components/ape-download-button/ape-download-button.service";
import { UserMenuItem } from "src/app/constants/utils-type";
import { ApeMonitoringDevelopmentPlanDetailsTemplateComponent } from "src/app/templates/process-monitoring-development-plan-details-template/process-monitoring-development-plan-details-template.component";

/**
 * Componente pagina Monitoraggio Admin details
 */
@Component({
  selector: "app-admin-monitoring-details",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ApeMonitoringDetailsTemplateComponent,
    ApeMonitoringDevelopmentPlanDetailsTemplateComponent,
    ApeToastComponent
  ],
  templateUrl: "./monitoring-details.component.html"
})
export class AdminMonitoringDetailsComponent extends BaseComponent implements OnInit, OnDestroy {
  private monitoringBackofficeApiService = inject(MonitoringBackofficeApiService);

  private translate = inject(TranslateService);

  private alertService = inject(AlertService);

  private impersonationService = inject(ImpersonationService);

  private monitoringCalibrationBackofficeApiService = inject(MonitoringCalibrationBackofficeApiService);

  private downloadService = inject(ApeDownloadButtonService);

  private destroy$ = new Subject<void>();

  @ViewChild(ApeToastComponent)
  private toastComponent?: ApeToastComponent;

  /**
   * Chiavi per salvare stato in sessione
   */
  static readonly SESSION_KEYS = {
    SEARCHED_TEXT: "monitoringDetails_searchedText",
    PAGE: "monitoringDetails_page",
    PAGE_SIZE: "monitoringDetails_pageSize",
    SELECTED_FEEDBACK_360_STATUSES: "monitoringDetails_selectedFeedback360Statuses",
    SELECTED_SELF_ASSESSMENT_STATUSES: "monitoringDetails_selectedSelfAssessmentStatuses",
    SELECTED_EVALUATION_STATUSES: "monitoringDetails_selectedEvaluationStatuses",
    SELECTED_DELIVERY_STATUSES: "monitoringDetails_selectedDeliveryStatuses",
    APPLIED_FEEDBACK_360_STATUSES: "monitoringDetails_appliedFeedback360Statuses",
    APPLIED_SELF_ASSESSMENT_STATUSES: "monitoringDetails_appliedSelfAssessmentStatuses",
    APPLIED_EVALUATION_STATUSES: "monitoringDetails_appliedEvaluationStatuses",
    APPLIED_DELIVERY_STATUSES: "monitoringDetails_appliedDeliveryStatuses",
    SELECTED_SORT_OPTION: "monitoringDetails_selectedSortOption",
    SELECTED_COMPANY_OPTIONS: "monitoringDetails_selectedCompanyOptions",
    APPLIED_COMPANY_OPTIONS: "monitoringDetails_appliedCompanyOptions",
    SELECTED_OFFICE_OPTIONS: "monitoringDetails_selectedOfficeOptions",
    APPLIED_OFFICE_OPTIONS: "monitoringDetails_appliedOfficeOptions",
    SELECTED_DEPARTMENT_OPTIONS: "monitoringDetails_selectedDepartmentOptions",
    APPLIED_DEPARTMENT_OPTIONS: "monitoringDetails_appliedDepartmentOptions",
    SELECTED_BU_OPTIONS: "monitoringDetails_selectedBUOptions",
    APPLIED_BU_OPTIONS: "monitoringDetails_appliedBUOptions",
    SELECTED_EVALUATOR_OPTIONS: "monitoringDetails_selectedEvaluatorOptions",
    APPLIED_EVALUATORS_OPTIONS: "monitoringDetails_appliedEvaluatorsOptions"
  } as const;

  // Stato di loading processo
  loading: boolean = true;

  // Stato di loading  dei dettagli del processo
  loadingProcessDetails: boolean = true;

  // Stato di loading utenti
  loadingUsersData: boolean = false;

  // Dettagli del processo
  processDetails: MonitoringSummary | MonitoringSummaryDevelopmentPlan | null = null;

  // ID del processo dalla route
  processId: string | null = null;

  processType: ProcessTypeEnum | null = null;

  // Opzioni per lo stato del processo
  processStatusOptions: MenuItem[] = [];

  // Validità del form - proprietà calcolata
  // Il form è valido solo se è valido secondo i validatori E se è stato modificato (dirty)
  isFormValid: boolean = false;

  userFilterData: MonitoringUserFiltersValues | null = null;

  usersDataCount: number = 0;

  loadingUsersDataCount: boolean = false;

  searchedText: string = "";

  feedback360StatusOptions: MenuItem[] = [];

  selfAssessmentStatusOptions: MenuItem[] = [];

  evaluationStatusOptions: MenuItem[] = [];

  deliveryStatusOptions: MenuItem[] = [];

  developmentPlanStatusOptions: MenuItem[] = [];

  companyOptions: MenuItem[] = [];

  officeOptions: MenuItem[] = [];

  departmentOptions: MenuItem[] = [];

  BUOptions: MenuItem[] = [];

  evaluatorsOptions: UserMenuItem[] = [];

  /** Filtri selezionati */
  selectedFeedback360Statuses: MenuItem[] = [];

  selectedSelfAssessmentStatuses: MenuItem[] = [];

  selectedEvaluationStatuses: MenuItem[] = [];

  selectedDeliveryStatuses: MenuItem[] = [];

  selectedDevelopmentPlanStatuses: MenuItem[] = [];

  selectedCompanyOptions: MenuItem[] = [];

  selectedEvaluatorOptions: MenuItem[] = [];

  selectedDepartmentOptions: MenuItem[] = [];

  selectedOfficeOptions: MenuItem[] = [];

  selectedBUOptions: MenuItem[] = [];

  /** Filtri applicati */
  appliedFeedback360Statuses: MenuItem[] = [];

  appliedSelfAssessmentStatuses: MenuItem[] = [];

  appliedEvaluationStatuses: MenuItem[] = [];

  appliedDeliveryStatuses: MenuItem[] = [];

  appliedCompanyOptions: MenuItem[] = [];

  appliedEvaluatorsOptions: MenuItem[] = [];

  appliedDepartmentOptions: MenuItem[] = [];

  appliedOfficeOptions: MenuItem[] = [];

  appliedBUOptions: MenuItem[] = [];

  appliedDevelopmentPlanStatuses: MenuItem[] = [];

  usersData: MonitoringUserSummaryExtended[] = [];

  allUsersData: MonitoringUserSummaryIdsExtended[] = [];

  loadingUserFilterData: boolean = false;

  monitoringDetailsCountersCardData: StatusCardData[] = [];

  processName: string = "";

  allUsersChecked: boolean = false;

  processPhases: BackofficeProcessPhases | null = null;

  processStatus: ProcessStatusEnum | null = null;

  pageSize: number = 50;

  page: number = 1;

  advancedFiltersDrawerVisible: boolean = false;

  readonly TRANSLATION_KEYS = TRANSLATION_KEYS;

  sortOptions: MenuItem[] = [];

  selectedSortOption: MenuItem | undefined = undefined;

  isLoadingAllUsersDataForChecked: boolean = false;

  showConfirmationModal: boolean = false;

  confirmationSummary: PersonSummary[] = [];

  isLoadingConfirmation: boolean = false;

  private calibrationUsers: TalentMatrixUser[] = [];

  skipFiltersForAllUsersData: boolean = false;

  protected get isEvaluationProcess(): boolean {
    if (!this.processType) {
      return false;
    }
    return this.processType === ProcessTypeEnumTypes.EVALUATION;
  }

  protected get isDevelopmentPlanProcess(): boolean {
    if (!this.processType) {
      return false;
    }
    return this.processType === ProcessTypeEnumTypes.DEVELOPMENT_PLAN;
  }

  override async ngOnInit(): Promise<void> {
    try {
      // Recupera l'ID del processo dalla route
      this.processId = this.route.snapshot.paramMap.get("processId");

      if (!this.processId) {
        this.logger.error("Process ID not found in route parameters");
        this.alertService.showError("ID processo non trovato");
        this.goBack();
        return;
      }

      this.sortOptions = [
        {
          id: MonitoringUserSortEnumTypes.SURNAME_ASC,
          title: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.details.order.SURNAME_ASC)
        },
        {
          id: MonitoringUserSortEnumTypes.SURNAME_DESC,
          title: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.details.order.SURNAME_DESC)
        }
      ];

      this.selectedSortOption = this.sortOptions[0];

      await this.getProcess();

      await this.loadProcessDetails();

      await this.getFilterData();

      // Controlla se ci sono dati di sessione da ripristinare (dopo aver caricato i filtri)
      this.restoreStateFromSession();

      // Carica i dati (con stato ripristinato se presente, altrimenti con valori di default)
      await this.getUsersDataCount();

      await this.getProcessPhases();
    } catch (error) {
      this.logger.error(`Error initializing process details: ${error?.toString()}`);
    } finally {
      this.loading = false;
    }
  }

  override ngOnDestroy(): Promise<void> {
    this.destroy$.next();
    this.destroy$.complete();
    return Promise.resolve();
  }

  private async getProcess(): Promise<void> {
    if (!this.processId) {
      this.logger.error("Process ID is required to get process");
      return;
    }
    const process = await this.monitoringBackofficeApiService.getProcess(this.processId);
    this.processName = process?.name || "";
    this.processStatus = process?.status || null;
    this.processType = process?.processType || null;
    // se processType è development plan, imposto skipFiltersForAllUsersData a true
    if (this.processType === ProcessTypeEnumTypes.DEVELOPMENT_PLAN) {
      this.skipFiltersForAllUsersData = true;
    }
  }

  private async getProcessPhases(): Promise<void> {
    if (!this.processId) {
      this.logger.error("Process ID is required to get process phases");
      return;
    }
    const processPhases = await this.monitoringBackofficeApiService.getProcessPhases(this.processId);
    this.processPhases = processPhases;
  }

  /**
   * Carica i dettagli del processo
   */
  private async loadProcessDetails(): Promise<void> {
    if (!this.processId) {
      this.logger.error("Process ID is required to load process details");
      return;
    }
    try {
      this.loadingProcessDetails = true;

      // Prepara i filtri applicati
      const filters: ProcessDetailsFilters = {};

      if (this.appliedFeedback360Statuses.length > 0) {
        filters.feedback360Status = this.appliedFeedback360Statuses.map(status => status.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedSelfAssessmentStatuses.length > 0) {
        filters.selfAssessmentStatus = this.appliedSelfAssessmentStatuses.map(status => status.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedEvaluationStatuses.length > 0) {
        filters.evaluationStatus = this.appliedEvaluationStatuses.map(status => status.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedDeliveryStatuses.length > 0) {
        filters.deliveryStatus = this.appliedDeliveryStatuses.map(status => status.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedDevelopmentPlanStatuses.length > 0) {
        filters.developmentPlanStatus = this.appliedDevelopmentPlanStatuses.map(status => status.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.searchedText) {
        filters.searchedText = this.searchedText;
      }
      if (this.appliedOfficeOptions.length > 0) {
        filters.office = this.appliedOfficeOptions.map(office => office.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedBUOptions.length > 0) {
        filters.BU = this.appliedBUOptions.map(BU => BU.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedDepartmentOptions.length > 0) {
        filters.department = this.appliedDepartmentOptions.map(department => department.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedCompanyOptions.length > 0) {
        filters.company = this.appliedCompanyOptions.map(company => company.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedEvaluatorsOptions.length > 0) {
        filters.evaluators = this.appliedEvaluatorsOptions.map(evaluator => evaluator.id)
          .filter((id): id is string => id != undefined);
      }

      this.processDetails = await this.monitoringBackofficeApiService.getProcessDetails(this.processId, filters);

      this.formatMonitoringSummaryData();
      this.logger.info("Process details loaded successfully");
    } catch (error) {
      this.logger.error(`Error loading process details: ${error?.toString()}`);
      this.alertService.showError("Errore nel caricamento dei dettagli processo");
      this.processDetails = null;
    } finally {
      this.loadingProcessDetails = false;
    }
  }

  private formatMonitoringSummaryData(): void {
    if (this.processType === ProcessTypeEnumTypes.EVALUATION) {
      this.formatMonitoringSummaryDataEvaluation();
    } else {
      this.formatMonitoringSummaryDataDevelopmentPlan();
    }
  }

  private formatMonitoringSummaryDataDevelopmentPlan(): void {
    if (!this.processDetails) {
      return;
    }
    const summary = this.processDetails as MonitoringSummaryDevelopmentPlan;
    this.monitoringDetailsCountersCardData = [
      {
        title: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.participants.INCLUDED_ON_PROCESS),
        value: summary.totIncludedUsers?.toLocaleString("it-IT") || "0",
        description: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.participants.INCLUDED_ON_PROCESS_DESCRIPTION),
        icon: "assets/icons/user-round-plus.svg"
      },
      {
        title: this.translate.instant<string>(TRANSLATION_KEYS.admin.monitoring.participants.EVALUATIONS),
        value: `${summary.totCompletedEvaluations?.toLocaleString("it-IT") || "0"}/${summary.totEvaluations?.toLocaleString("it-IT") || "0"}`,
        description: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.participants.EVALUATIONS_DESCRIPTION),
        icon: "assets/icons/user-round.svg",
        percentage: summary.totEvaluations && summary.totCompletedEvaluations && summary.totEvaluations > 0
          ? ((summary.totCompletedEvaluations / summary.totEvaluations) * 100).toString()
          : "0"
      },
      {
        title: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.participants.DEVELOPMENT_PLANS_PROGRESS),
        value: `${summary.startedMandatoryDevelopmentPlans?.toLocaleString("it-IT") || "0"}/${summary.totMandatoryDevelopmentPlans?.toLocaleString("it-IT") || "0"}`,
        description: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.participants.DEVELOPMENT_PLANS_PROGRESS_DESCRIPTION),
        icon: "assets/icons/loader-circle.svg",
        percentage: summary.totMandatoryDevelopmentPlans && summary.startedMandatoryDevelopmentPlans && summary.totMandatoryDevelopmentPlans > 0
          ? ((summary.startedMandatoryDevelopmentPlans / summary.totMandatoryDevelopmentPlans) * 100).toString()
          : "0"
      },
      {
        title: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.participants.DEVELOPMENT_PLANS_COMPLETION),
        value: `${summary.completedMandatoryDevelopmentPlans?.toLocaleString("it-IT") || "0"}/${summary.totMandatoryDevelopmentPlans?.toLocaleString("it-IT") || "0"}`,
        description: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.participants.DEVELOPMENT_PLANS_COMPLETION_DESCRIPTION),
        icon: "assets/icons/loader-circle.svg",
        percentage: summary.totMandatoryDevelopmentPlans && summary.completedMandatoryDevelopmentPlans && summary.totMandatoryDevelopmentPlans > 0
          ? ((summary.completedMandatoryDevelopmentPlans / summary.totMandatoryDevelopmentPlans) * 100).toString()
          : "0"
      }
    ];
  }

  private formatMonitoringSummaryDataEvaluation(): void {
    if (!this.processDetails) {
      return;
    }
    const summary = this.processDetails as MonitoringSummary;
    this.monitoringDetailsCountersCardData = [
      {
        title: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.participants.INCLUDED_ON_PROCESS),
        value: summary.totIncludedUsers?.toLocaleString("it-IT") || "0",
        description: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.participants.INCLUDED_ON_PROCESS_DESCRIPTION),
        icon: "assets/icons/user-round-plus.svg",
        withTooltip: true,
        totEvaluations: summary.totEvaluations || 0,
        totSelfAssessments: summary.totSelfAssessments || 0,
        scorecardsProfessional: summary.scorecardsProfessional || 0,
        scorecardsOperations: summary.scorecardsOperations || 0
      },
      {
        title: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.participants.AUTOEVALUATIONS),
        value: (summary.completedSelfAssessments.toLocaleString("it-IT") + "/" + summary.totSelfAssessments.toLocaleString("it-IT")) || "0/0",
        description: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.participants.AUTOEVALUATIONS_DESCRIPTION),
        icon: "assets/icons/user-round.svg",
        percentage: summary.totSelfAssessments > 0
          ? ((summary.completedSelfAssessments / summary.totSelfAssessments) * 100).toFixed(2)
          : "0"
      },
      {
        title: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.participants.EVALUATIONS),
        value: (summary.completedEvaluations.toLocaleString("it-IT") + "/" + summary.totEvaluations.toLocaleString("it-IT")) || "0/0",
        description: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.participants.EVALUATIONS_DESCRIPTION),
        icon: "assets/icons/users-round.svg",
        percentage: summary.totEvaluations > 0
          ? ((summary.completedEvaluations / summary.totEvaluations) * 100).toFixed(2)
          : "0"
      },
      {
        title: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.participants.DELIVERY),
        value: (summary.completedDeliveryPhases.toLocaleString("it-IT") + "/" + summary.totDeliveryPhases.toLocaleString("it-IT")) || "0/0",
        description: this.translate.instant(TRANSLATION_KEYS.admin.monitoring.participants.DELIVERY_DESCRIPTION),
        icon: "assets/icons/user-round-check.svg",
        percentage: summary.totDeliveryPhases > 0
          ? ((summary.completedDeliveryPhases / summary.totDeliveryPhases) * 100).toFixed(2)
          : "0"
      }
    ];
  }

  private async getUsersDataCount(): Promise<void> {
    if (!this.processId) {
      this.logger.error("Process ID is required to load users data");
      return;
    }
    try {
      this.loadingUsersDataCount = true;
      this.logger.info(`Loading users data for ID: ${this.processId}`);

      const usersCountParams: UsersCountParams = {
        searchedText: this.searchedText || "",
        processId: this.processId
      };

      if (this.selectedFeedback360Statuses.length > 0) {
        const feedback360Statuses = this.selectedFeedback360Statuses;
        usersCountParams.feedback360Status = feedback360Statuses.map(status => status.id) as string[];
      }

      if (this.selectedEvaluationStatuses.length > 0) {
        const evaluationStatuses = this.selectedEvaluationStatuses;
        usersCountParams.evaluationStatus = evaluationStatuses.map(status => status.id) as string[];
      }
      if (this.selectedSelfAssessmentStatuses.length > 0) {
        const selfAssessmentStatuses = this.selectedSelfAssessmentStatuses;
        usersCountParams.selfAssessmentStatus = selfAssessmentStatuses.map(status => status.id) as string[];
      }
      if (this.selectedDeliveryStatuses.length > 0) {
        const deliveryStatuses = this.selectedDeliveryStatuses;
        usersCountParams.deliveryStatus = deliveryStatuses.map(status => status.id) as string[];
      }
      if (this.selectedCompanyOptions.length > 0) {
        const companyOptions = this.selectedCompanyOptions;
        usersCountParams.company = companyOptions.map(company => company.id) as string[];
      }
      if (this.selectedEvaluatorOptions.length > 0) {
        const evaluatorOptions = this.selectedEvaluatorOptions;
        usersCountParams.evaluators = evaluatorOptions.map(evaluator => evaluator.id) as string[];
      }
      if (this.selectedDepartmentOptions.length > 0) {
        const departmentOptions = this.selectedDepartmentOptions;
        usersCountParams.department = departmentOptions.map(department => department.id) as string[];
      }
      if (this.selectedOfficeOptions.length > 0) {
        const officeOptions = this.selectedOfficeOptions;
        usersCountParams.office = officeOptions.map(office => office.id) as string[];
      }
      if (this.selectedBUOptions.length > 0) {
        const BUOptions = this.selectedBUOptions;
        usersCountParams.BU = BUOptions.map(BU => BU.id) as string[];
      }
      if (this.appliedDevelopmentPlanStatuses.length > 0) {
        usersCountParams.developmentPlanStatus = this.appliedDevelopmentPlanStatuses.map(status => status.id)
          .filter((id): id is string => id != undefined);
      }
      sessionStorage.removeItem("userIdsChecked");

      this.usersDataCount = await this.monitoringBackofficeApiService.getUsersDataCount(usersCountParams);

      if (this.usersDataCount > 0) {
        await this.getUsersData();
        await this.getAllUsersDataForChecked();
      } else {
        this.usersData = [];
        this.allUsersData = [];
        this.logger.info("No users found, skipping users data loading");
      }

      this.logger.info("Users data loaded successfully");
    } catch (error) {
      this.logger.error(`Error loading users data: ${error?.toString()}`);
      this.alertService.showError("Errore nel caricamento dei dati utente");
      this.usersDataCount = 0;
      this.usersData = [];
      this.allUsersData = [];
    } finally {
      this.loadingUsersDataCount = false;
    }
  }

  private async getAllUsersDataForChecked(): Promise<void> {
    if (!this.processId) {
      this.logger.error("Process ID is required to load users data");
      return;
    }
    try {
      this.isLoadingAllUsersDataForChecked = true;
      this.logger.info(`Loading users data for ID: ${this.processId}`);

      if (this.skipFiltersForAllUsersData) {
        this.allUsersData = await this.monitoringBackofficeApiService.getAllUsersDataForChecked({
          processId: this.processId,
          searchedText: ""
        });
        return;
      }

      const allUsersDataForCheckedParams: AllUsersDataForCheckedParams = {
        searchedText: this.searchedText || "",
        processId: this.processId
      };

      // Usa i filtri applicati, non quelli selezionati
      if (this.appliedSelfAssessmentStatuses.length > 0) {
        allUsersDataForCheckedParams.selfAssessmentStatus = this.appliedSelfAssessmentStatuses
          .map(status => status.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedEvaluationStatuses.length > 0) {
        allUsersDataForCheckedParams.evaluationStatus = this.appliedEvaluationStatuses
          .map(status => status.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedFeedback360Statuses.length > 0) {
        allUsersDataForCheckedParams.feedback360Status = this.appliedFeedback360Statuses
          .map(status => status.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedDeliveryStatuses.length > 0) {
        allUsersDataForCheckedParams.deliveryStatus = this.appliedDeliveryStatuses
          .map(status => status.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedDevelopmentPlanStatuses.length > 0) {
        allUsersDataForCheckedParams.developmentPlanStatus = this.appliedDevelopmentPlanStatuses
          .map(status => status.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedOfficeOptions.length > 0) {
        allUsersDataForCheckedParams.office = this.appliedOfficeOptions
          .map(office => office.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedBUOptions.length > 0) {
        allUsersDataForCheckedParams.BU = this.appliedBUOptions
          .map(BU => BU.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedDepartmentOptions.length > 0) {
        allUsersDataForCheckedParams.department = this.appliedDepartmentOptions
          .map(department => department.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedCompanyOptions.length > 0) {
        allUsersDataForCheckedParams.company = this.appliedCompanyOptions
          .map(company => company.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.appliedEvaluatorsOptions.length > 0) {
        allUsersDataForCheckedParams.evaluators = this.appliedEvaluatorsOptions
          .map(evaluator => evaluator.id)
          .filter((id): id is string => id != undefined);
      }

      this.allUsersData = await this.monitoringBackofficeApiService.getAllUsersDataForChecked(allUsersDataForCheckedParams);
    } catch (error) {
      this.logger.error(`Error loading users data: ${error?.toString()}`);
      this.alertService.showError("Errore nel caricamento dei dati utente");
      this.allUsersData = [];
    } finally {
      this.isLoadingAllUsersDataForChecked = false;
    }
  }

  private async getUsersData(checkUsersChecked?: boolean): Promise<void> {
    if (!this.processId) {
      this.logger.error("Process ID is required to load users data");
      return;
    }
    try {
      this.loadingUsersData = true;
      this.logger.info(`Loading users data for ID: ${this.processId}`);

      // Calcola i parametri di paginazione
      const fromRecord = (this.page - 1) * this.pageSize;
      const numRecords = this.pageSize;

      const usersDataParams: UsersDataParams = {
        searchedText: this.searchedText || "",
        processId: this.processId,
        fromRecord: fromRecord,
        numRecords: numRecords,
        sortBy: this.selectedSortOption?.id
      };

      if (this.selectedSelfAssessmentStatuses.length > 0) {
        const selfAssessmentStatuses = this.selectedSelfAssessmentStatuses;
        usersDataParams.selfAssessmentStatus = selfAssessmentStatuses.map(status => status.id) as string[];
      }
      if (this.selectedEvaluationStatuses.length > 0) {
        const evaluationStatuses = this.selectedEvaluationStatuses;
        usersDataParams.evaluationStatus = evaluationStatuses.map(status => status.id) as string[];
      }
      if (this.selectedFeedback360Statuses.length > 0) {
        const feedback360Statuses = this.selectedFeedback360Statuses;
        usersDataParams.feedback360Status = feedback360Statuses.map(status => status.id) as string[];
      }
      if (this.selectedDeliveryStatuses.length > 0) {
        const deliveryStatuses = this.selectedDeliveryStatuses;
        usersDataParams.deliveryStatus = deliveryStatuses.map(status => status.id) as string[];
      }
      if (this.appliedDevelopmentPlanStatuses.length > 0) {
        usersDataParams.developmentPlanStatus = this.appliedDevelopmentPlanStatuses
          .map(status => status.id)
          .filter((id): id is string => id != undefined);
      }
      if (this.selectedCompanyOptions.length > 0) {
        const companyOptions = this.selectedCompanyOptions;
        usersDataParams.company = companyOptions.map(company => company.id) as string[];
      }
      if (this.selectedEvaluatorOptions.length > 0) {
        const evaluatorOptions = this.selectedEvaluatorOptions;
        usersDataParams.evaluators = evaluatorOptions.map(evaluator => evaluator.id) as string[];
      }
      if (this.selectedDepartmentOptions.length > 0) {
        const departmentOptions = this.selectedDepartmentOptions;
        usersDataParams.department = departmentOptions.map(department => department.id) as string[];
      }
      if (this.selectedOfficeOptions.length > 0) {
        const officeOptions = this.selectedOfficeOptions;
        usersDataParams.office = officeOptions.map(office => office.id) as string[];
      }
      if (this.selectedBUOptions.length > 0) {
        const BUOptions = this.selectedBUOptions;
        usersDataParams.BU = BUOptions.map(BU => BU.id) as string[];
      }

      this.usersData = await this.monitoringBackofficeApiService.getUsersData(usersDataParams);

      if (checkUsersChecked) {
        for (const user of this.allUsersData) {
          const userData = this.usersData.find(p => p.userId === user.userId);
          if (userData) {
            userData.checked = user.checked || false;
          }
        }
      }
    } catch (error) {
      this.logger.error(`Error loading users data: ${error?.toString()}`);
      this.alertService.showError("Errore nel caricamento dei dati utente");
      this.usersData = [];
      this.allUsersData = [];
    } finally {
      this.loadingUsersData = false;
    }
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.usersDataCount / this.pageSize));
  }

  onPageChange(page: number): void {
    this.logger.info(`Changing page from ${this.page} to ${page}`);
    this.page = page;
    void this.getUsersData(true);
  }

  /**
   * Gestisce il cambio della dimensione della pagina
   * @param pageSize - Nuova dimensione della pagina
   */
  onPageSizeChange(pageSize: number): void {
    this.logger.info(`Changing page size from ${this.pageSize} to ${pageSize}`);
    this.pageSize = pageSize;
    this.page = 1; // Reset alla prima pagina
    void this.getUsersData(true);
  }

  private async getFilterData(): Promise<void> {
    try {
      this.logger.info(`Loading user filters values for ID: ${this.processId}`);
      if (!this.processId) {
        this.logger.error("Process ID is required to load user filters values");
        return;
      }

      this.loadingUserFilterData = true;

      this.userFilterData = await this.monitoringBackofficeApiService.getMonitoringUserFiltersValues(this.processId);
      if (this.userFilterData?.feedback360Status?.length) {
        this.feedback360StatusOptions = this.userFilterData.feedback360Status.map(status => ({
          id: status,
          title: this.translate.instant(`admin.monitoring.details.filter.feedback360Status.${status}`),
          label: this.translate.instant(`admin.monitoring.details.filter.${status}`)
        }));
      }
      if (this.userFilterData?.selfAssessmentStatus?.length) {
        this.selfAssessmentStatusOptions = this.userFilterData.selfAssessmentStatus.map(status => ({
          id: status,
          title: this.translate.instant(`admin.monitoring.details.filter.selfAssessmentStatus.${status}`),
          label: this.translate.instant(`admin.monitoring.details.filter.${status}`)
        }));
      }
      if (this.userFilterData?.evaluationStatus?.length) {
        this.evaluationStatusOptions = this.userFilterData.evaluationStatus.map(status => ({
          id: status,
          title: this.translate.instant(`admin.monitoring.details.filter.evaluationStatus.${status}`),
          label: this.translate.instant(`admin.monitoring.details.filter.${status}`)
        }));
      }
      if (this.userFilterData?.deliveryStatus?.length) {
        this.deliveryStatusOptions = this.userFilterData.deliveryStatus.map(status => ({
          id: status,
          title: this.translate.instant(`admin.monitoring.details.filter.deliveryStatus.${status}`),
          label: this.translate.instant(`admin.monitoring.details.filter.${status}`)
        }));
      }
      if (this.userFilterData?.developmentPlanStatus?.length) {
        this.developmentPlanStatusOptions = this.userFilterData.developmentPlanStatus.map(status => ({
          id: status,
          title: this.translate.instant(`admin.monitoring.details.filter.developmentPlanStatus.${status}`),
          label: this.translate.instant(`admin.monitoring.details.filter.${status}`)
        }));
      }
      if (this.userFilterData?.company?.length) {
        this.companyOptions = this.userFilterData.company.map(company => ({
          id: company,
          title: company,
          label: company
        }));
      }
      if (this.userFilterData?.office?.length) {
        this.officeOptions = this.userFilterData.office.map(office => ({
          id: office,
          title: office,
          label: office
        }));
      }
      if (this.userFilterData?.department?.length) {
        this.departmentOptions = this.userFilterData.department.map(department => ({
          id: department,
          title: department,
          label: department
        }));
      }
      if (this.userFilterData?.BU?.length) {
        this.BUOptions = this.userFilterData.BU.map(BU => ({
          id: BU,
          title: BU,
          label: BU
        }));
      }
      if (this.userFilterData?.evaluators?.length) {
        this.evaluatorsOptions = this.userFilterData.evaluators.map(evaluator => ({
          id: evaluator.userId,
          label: `${evaluator.forename} ${evaluator.surname}`,
          title: `${evaluator.forename} ${evaluator.surname}`,
          forename: evaluator.forename,
          surname: evaluator.surname,
          role: evaluator.role,
          email: evaluator.email
        }));
      }

      this.loadingUserFilterData = false;

      this.logger.info("User filters values loaded successfully");
    } catch (error) {
      this.logger.error(`Error loading user filters values: ${error?.toString()}`);
      this.alertService.showError("Errore nel caricamento dei filtri utente");
      this.userFilterData = null;
    }
  }

  closeDrawer(): void {
    this.advancedFiltersDrawerVisible = false;
  }

  onFeedback360StatusesChange(selected: MenuItem[]): void {
    this.selectedFeedback360Statuses = selected;
  }

  onSelfAssessmentStatusesChange(selected: MenuItem[]): void {
    this.selectedSelfAssessmentStatuses = selected;
  }

  onEvaluationStatusesChange(selected: MenuItem[]): void {
    this.selectedEvaluationStatuses = selected;
  }

  onDeliveryStatusesChange(selected: MenuItem[]): void {
    this.selectedDeliveryStatuses = selected;
  }

  onDevelopmentPlanStatusesChange(selected: MenuItem[]): void {
    this.selectedDevelopmentPlanStatuses = selected;
  }

  onCompanyChange(selected: MenuItem[]): void {
    this.selectedCompanyOptions = selected;
  }

  onEvaluatorChange(selected: MenuItem[]): void {
    this.selectedEvaluatorOptions = selected;
  }

  onDepartmentChange(selected: MenuItem[]): void {
    this.selectedDepartmentOptions = selected;
  }

  onOfficeChange(selected: MenuItem[]): void {
    this.selectedOfficeOptions = selected;
  }

  onBUChange(selected: MenuItem[]): void {
    this.selectedBUOptions = selected;
  }

  applyFilters(): void {
    this.resetPagination();
    this.logger.info("Applying filters");
    // Aggiorno gli applied filter con i selectedFilter
    this.appliedFeedback360Statuses = this.selectedFeedback360Statuses;
    this.appliedSelfAssessmentStatuses = this.selectedSelfAssessmentStatuses;
    this.appliedEvaluationStatuses = this.selectedEvaluationStatuses;
    this.appliedDeliveryStatuses = this.selectedDeliveryStatuses;
    this.appliedCompanyOptions = this.selectedCompanyOptions;
    this.appliedEvaluatorsOptions = this.selectedEvaluatorOptions;
    this.appliedDepartmentOptions = this.selectedDepartmentOptions;
    this.appliedOfficeOptions = this.selectedOfficeOptions;
    this.appliedBUOptions = this.selectedBUOptions;
    this.appliedDevelopmentPlanStatuses = this.selectedDevelopmentPlanStatuses;
    this.advancedFiltersDrawerVisible = false;
    void this.loadProcessDetails();
    void this.getUsersDataCount();
  }

  /**
     * Torna alla lista dei processi
    */
  goBack(): void {
    this.logger.info("Going back to process setup list");
    void this.router.navigate([UrlCollection.adminMonitoring]);
  }

  onAdvancedFiltersClicked(): void {
    this.logger.info("Advanced filters clicked");
    this.advancedFiltersDrawerVisible = true;
  }

  resetFilters(): void {
    this.resetPagination();
    this.appliedFeedback360Statuses = [];
    this.appliedSelfAssessmentStatuses = [];
    this.appliedEvaluationStatuses = [];
    this.appliedDeliveryStatuses = [];
    this.selectedFeedback360Statuses = [];
    this.selectedSelfAssessmentStatuses = [];
    this.selectedEvaluationStatuses = [];
    this.selectedDeliveryStatuses = [];
    this.selectedCompanyOptions = [];
    this.selectedEvaluatorOptions = [];
    this.selectedDepartmentOptions = [];
    this.selectedOfficeOptions = [];
    this.selectedBUOptions = [];
    this.appliedCompanyOptions = [];
    this.appliedEvaluatorsOptions = [];
    this.appliedDepartmentOptions = [];
    this.appliedOfficeOptions = [];
    this.appliedBUOptions = [];
    this.appliedDevelopmentPlanStatuses = [];
    void this.loadProcessDetails();
    void this.getUsersDataCount();
    this.closeDrawer();
  }

  onDrawerVisibleChange(event: boolean): void {
    this.logger.info(`Drawer visible changed to: ${event}`);
    this.advancedFiltersDrawerVisible = event;
  }

  onSearchChange(event: string): void {
    this.searchedText = event;
  }

  onSearchUsers(): void {
    this.resetPagination();
    this.logger.info(`Searching for: ${this.searchedText}`);
    void this.loadProcessDetails();
    void this.getUsersDataCount();
  }

  resetPagination(): void {
    this.page = 1;
    this.usersData = [];
    this.allUsersData = [];
    this.usersDataCount = 0;
  }

  onSortChange(event: MenuItem): void {
    this.logger.info(`Sort changed to: ${event.id}`);
    this.selectedSortOption = event;
    this.page = 1;
    void this.getUsersData(true);
  }

  onRemoveFilterFromPage(filter: MenuItem): void {
    this.logger.info(`Removing filter from page: ${filter.label}`);
    // Rimuovi dai filtri applicati (controlla sia id che label per evitare conflitti tra categorie diverse)
    this.appliedFeedback360Statuses = this.appliedFeedback360Statuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.appliedSelfAssessmentStatuses = this.appliedSelfAssessmentStatuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.appliedEvaluationStatuses = this.appliedEvaluationStatuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.appliedDeliveryStatuses = this.appliedDeliveryStatuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));

    // Rimuovi dai filtri organizzativi applicati
    this.appliedCompanyOptions = this.appliedCompanyOptions.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.appliedDepartmentOptions = this.appliedDepartmentOptions.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.appliedOfficeOptions = this.appliedOfficeOptions.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.appliedBUOptions = this.appliedBUOptions.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.appliedDevelopmentPlanStatuses = this.appliedDevelopmentPlanStatuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));

    // Rimuovi dai filtri selezionati (controlla sia id che label)
    this.selectedFeedback360Statuses = this.selectedFeedback360Statuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedSelfAssessmentStatuses = this.selectedSelfAssessmentStatuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedEvaluationStatuses = this.selectedEvaluationStatuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedDeliveryStatuses = this.selectedDeliveryStatuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedCompanyOptions = this.selectedCompanyOptions.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedEvaluatorOptions = this.selectedEvaluatorOptions.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedDepartmentOptions = this.selectedDepartmentOptions.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedOfficeOptions = this.selectedOfficeOptions.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedBUOptions = this.selectedBUOptions.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedDevelopmentPlanStatuses = this.selectedDevelopmentPlanStatuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.resetPagination();
    void this.loadProcessDetails();
    void this.getUsersDataCount();
  }

  async onReopenEvaluation(user: MonitoringUserSummaryExtended | MonitoringUserDevelopmentPlanSummaryExtended): Promise<void> {
    this.logger.info(`Reopening evaluation for user ${user.userId}`);
    if (!this.processId || !user.userId) {
      this.logger.error(`Cannot reopen evaluation missing processId (${this.processId}) or userId (${user.userId})`);
    } else {
      try {
        await this.monitoringBackofficeApiService.reopenUserEvaluation(this.processId, user.userId);
        this.logger.info(`Evaluation reopened for user ${user.userId}`);
        this.alertService.showSuccess(this.translate.instant("admin.monitoring.details.REOPEN_EVALUATION_SUCCESS"));
        // reload the users data
        await this.getUsersData(true);
        await this.getAllUsersDataForChecked();
      } catch (error) {
        this.logger.error(`Error reopening evaluation for user ${user.userId}: ${error?.toString()}`);
        this.alertService.showError(this.translate.instant("admin.monitoring.details.REOPEN_EVALUATION_ERROR"));
      }
    }
  }

  async onReopenSelfEvaluation(user: MonitoringUserSummaryExtended): Promise<void> {
    this.logger.info(`Reopening self evaluation for user ${user.userId}`);
    if (!this.processId || !user.userId) {
      this.logger.error(`Cannot reopen self evaluation missing processId (${this.processId}) or userId (${user.userId})`);
    } else {
      try {
        await this.monitoringBackofficeApiService.reopenUserSelfEvaluation(this.processId, user.userId);
        this.logger.info(`Self evaluation reopened for user ${user.userId}`);
        this.alertService.showSuccess(this.translate.instant("admin.monitoring.details.REOPEN_SELF_EVALUATION_SUCCESS"));
        // reload the users data
        await this.getUsersData(true);
        await this.getAllUsersDataForChecked();
      } catch (error) {
        this.logger.error(`Error reopening self evaluation for user ${user.userId}: ${error?.toString()}`);
        this.alertService.showError(this.translate.instant("admin.monitoring.details.REOPEN_SELF_EVALUATION_ERROR"));
      }
    }
  }

  async onSolicitEvaluation(user: MonitoringUserSummaryExtended | MonitoringUserDevelopmentPlanSummaryExtended): Promise<void> {
    this.logger.info(`Soliciting evaluation for user ${user.userId}`);
    if (!this.processId || !user.userId) {
      this.logger.error(`Cannot solicit evaluation missing processId (${this.processId}) or userId (${user.userId})`);
    } else {
      try {
        await this.monitoringBackofficeApiService.solicitUserEvaluation(this.processId, user.userId);
        this.logger.info(`Evaluation solicited for user ${user.userId}`);
        this.toastComponent?.showToast("success", this.translate.instant("admin.monitoring.details.SOLICIT_EVALUATION_SUCCESS_TITLE"), this.translate.instant("admin.monitoring.details.SOLICIT_EVALUATION_SUCCESS"), "assets/icons/circle-check-big.svg");
      } catch (error) {
        this.logger.error(`Error soliciting evaluation for user ${user.userId}: ${error?.toString()}`);
        this.alertService.showError(this.translate.instant("admin.monitoring.details.SOLICIT_MASSIVE_EVALUATION_ERROR"));
      }
    }
  }

  async onSolicitSelfEvaluation(user: MonitoringUserSummaryExtended): Promise<void> {
    this.logger.info(`Soliciting self evaluation for user ${user.userId}`);
    if (!this.processId || !user.userId) {
      this.logger.error(`Cannot solicit self evaluation missing processId (${this.processId}) or userId (${user.userId})`);
    } else {
      try {
        await this.monitoringBackofficeApiService.solicitUserSelfEvaluation(this.processId, user.userId);
        this.logger.info(`Self evaluation solicited for user ${user.userId}`);
        this.toastComponent?.showToast("success", this.translate.instant("admin.monitoring.details.SOLICIT_SELF_EVALUATION_SUCCESS_TITLE"), this.translate.instant("admin.monitoring.details.SOLICIT_SELF_EVALUATION_SUCCESS"), "assets/icons/circle-check-big.svg");
      } catch (error) {
        this.logger.error(`Error soliciting self evaluation for user ${user.userId}: ${error?.toString()}`);
        this.alertService.showError(this.translate.instant("admin.monitoring.details.SOLICIT_MASSIVE_SELF_EVALUATION_ERROR"));
      }
    }
  }

  onSolicitMassiveEvaluation(): void {
    this.toastComponent?.showToast("success", this.translate.instant("admin.monitoring.details.SOLICIT_MASSIVE_EVALUATION_SUCCESS_TITLE"), this.translate.instant("admin.monitoring.details.SOLICIT_MASSIVE_EVALUATION_SUCCESS"), "assets/icons/circle-check-big.svg");
  }

  onSolicitMassiveSelfEvaluation(): void {
    this.toastComponent?.showToast("success", this.translate.instant("admin.monitoring.details.SOLICIT_MASSIVE_SELF_EVALUATION_SUCCESS_TITLE"), this.translate.instant("admin.monitoring.details.SOLICIT_MASSIVE_SELF_EVALUATION_SUCCESS"), "assets/icons/circle-check-big.svg");
  }

  onSolicitMassiveDevelopmentPlan(): void {
    this.toastComponent?.showToast("success", this.translate.instant("admin.monitoring.details.SOLICIT_MASSIVE_DEVELOPMENT_PLAN_SUCCESS_TITLE"), this.translate.instant("admin.monitoring.details.SOLICIT_MASSIVE_DEVELOPMENT_PLAN_SUCCESS"), "assets/icons/circle-check-big.svg");
  }

  protected async onConfirmSolicitDevelopmentPlan(user: MonitoringUserDevelopmentPlanSummaryExtended): Promise<void> {
    if (!user) {
      this.logger.error("Cannot solicit development plan missing user");
      return;
    }
    this.logger.info(`Soliciting development plan for user ${user.userId}`);
    if (!this.processId || !user.userId) {
      this.logger.error(`Cannot solicit development plan missing processId (${this.processId}) or userId (${user.userId})`);
    } else {
      try {
        await this.monitoringBackofficeApiService.solicitUserDevelopmentPlan(this.processId, user.userId);
        this.logger.info(`Development plan solicited for user ${user.userId}`);
        this.toastComponent?.showToast("success", this.translate.instant("admin.monitoring.details.SOLICIT_MASSIVE_DEVELOPMENT_PLAN_SUCCESS_TITLE"), this.translate.instant("admin.monitoring.details.SOLICIT_DEVELOPMENT_PLAN_SUCCESS"), "assets/icons/circle-check-big.svg");
      } catch (error) {
        this.logger.error(`Error soliciting development plan for user ${user.userId}: ${error?.toString()}`);
        this.alertService.showError(this.translate.instant("admin.monitoring.details.SOLICIT_MASSIVE_DEVELOPMENT_PLAN_ERROR"));
      }
    }
  }

  /**
   * Gestisce l'impersonazione dell'utente
   */
  async onDownloadEvaluationCard(user: MonitoringUserSummaryExtended): Promise<void> {
    this.logger.info(`Downloading evaluation card for user ${user.userId}`);
    if (!this.processId || !user.userId) {
      this.logger.error(`Cannot download evaluation card missing processId (${this.processId}) or userId (${user.userId})`);
    }
    try {
      const responseFile = await this.monitoringBackofficeApiService.downloadAdminReport(this.processId!, user.userId);
      if (responseFile) {
        const a = document.createElement("a");
        a.href = responseFile.fileUrl;
        a.download = responseFile.fileName;
        a.click();
        URL.revokeObjectURL(a.href);
      }
      this.logger.info(`Evaluation card downloaded for user ${user.userId}`);
    } catch (error) {
      this.logger.error(`Error downloading evaluation card for user ${user.userId}: ${error?.toString()}`);
      this.alertService.showError(this.translate.instant("admin.monitoring.details.DOWNLOAD_EVALUATION_CARD_ERROR"));
    }
  }

  async onImpersonateUser(user: {
    userId: string;
    forename: string;
    surname: string;
  }): Promise<void> {
    this.logger.info(`Impersonating user: ${user.forename} ${user.surname}`);

    // Salva lo stato corrente prima dell'impersonazione
    this.saveCurrentState();

    await this.impersonationService.handleImpersonation(user, user.userId, {
      path: UrlCollection.adminMonitoring,
      processId: this.processId ?? undefined
    });
  }

  /**
   * Gestisce l'impersonazione del manager
   */
  async onImpersonateManager(user: {
    userId: string;
    forename: string;
    surname: string;
    impersonableManagerId: string;
  }): Promise<void> {
    if (!user.impersonableManagerId) {
      this.logger.error(`Manager ID not available for user ${user.userId}`);
      return;
    }

    this.logger.info(`Impersonating manager of user: ${user.forename} ${user.surname}`);

    // Salva lo stato corrente prima dell'impersonazione
    this.saveCurrentState();

    await this.impersonationService.handleImpersonation(user, user.impersonableManagerId, {
      path: UrlCollection.adminMonitoring,
      processId: this.processId ?? undefined
    });
  }

  /**
   * Salva lo stato corrente nella sessione
   */
  private saveCurrentState(): void {
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SEARCHED_TEXT, this.searchedText);
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.PAGE, this.page.toString());
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.PAGE_SIZE, this.pageSize.toString());

    // Salva direttamente gli oggetti completi dei filtri
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_FEEDBACK_360_STATUSES, JSON.stringify(this.selectedFeedback360Statuses));
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_SELF_ASSESSMENT_STATUSES, JSON.stringify(this.selectedSelfAssessmentStatuses));
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_EVALUATION_STATUSES, JSON.stringify(this.selectedEvaluationStatuses));
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_DELIVERY_STATUSES, JSON.stringify(this.selectedDeliveryStatuses));

    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_FEEDBACK_360_STATUSES, JSON.stringify(this.appliedFeedback360Statuses));
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_SELF_ASSESSMENT_STATUSES, JSON.stringify(this.appliedSelfAssessmentStatuses));
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_EVALUATION_STATUSES, JSON.stringify(this.appliedEvaluationStatuses));
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_DELIVERY_STATUSES, JSON.stringify(this.appliedDeliveryStatuses));

    // Salva filtri avanzati (company, office, department, BU, evaluators)
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_COMPANY_OPTIONS, JSON.stringify(this.selectedCompanyOptions));
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_COMPANY_OPTIONS, JSON.stringify(this.appliedCompanyOptions));
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_OFFICE_OPTIONS, JSON.stringify(this.selectedOfficeOptions));
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_OFFICE_OPTIONS, JSON.stringify(this.appliedOfficeOptions));
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_DEPARTMENT_OPTIONS, JSON.stringify(this.selectedDepartmentOptions));
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_DEPARTMENT_OPTIONS, JSON.stringify(this.appliedDepartmentOptions));
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_BU_OPTIONS, JSON.stringify(this.selectedBUOptions));
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_BU_OPTIONS, JSON.stringify(this.appliedBUOptions));
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_EVALUATOR_OPTIONS, JSON.stringify(this.selectedEvaluatorOptions));
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_EVALUATORS_OPTIONS, JSON.stringify(this.appliedEvaluatorsOptions));

    // Salva l'ordinamento
    sessionStorage.setItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_SORT_OPTION, JSON.stringify(this.selectedSortOption));
  }

  private hasDataToRestore = false;

  /**
   * Helper per gestire il parsing JSON con logging
   */
  private parseWithLogging<T>(data: string, context: string): T | null {
    try {
      const result = JSON.parse(data) as T;
      this.hasDataToRestore = true;
      return result;
    } catch (e) {
      this.logger.error(`Error parsing ${context} from session`, e instanceof Error
        ? e.message
        : String(e));
      return null;
    }
  }

  /**
   * Ripristina lo stato dalla sessione
   */
  private restoreStateFromSession(): void {
    this.hasDataToRestore = false;

    // Ripristina testo di ricerca
    const savedSearchedText = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SEARCHED_TEXT);
    if (savedSearchedText !== null) {
      this.searchedText = savedSearchedText;
      this.hasDataToRestore = true;
    }

    // Ripristina paginazione
    const savedPage = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.PAGE);
    if (savedPage !== null) {
      this.page = parseInt(savedPage, 10);
      this.hasDataToRestore = true;
    }

    const savedPageSize = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.PAGE_SIZE);
    if (savedPageSize !== null) {
      this.pageSize = parseInt(savedPageSize, 10);
      this.hasDataToRestore = true;
    }

    // Ripristina filtri selezionati
    const savedSelectedFeedback360Statuses = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_FEEDBACK_360_STATUSES);
    if (savedSelectedFeedback360Statuses !== null) {
      this.selectedFeedback360Statuses = this.parseWithLogging<MenuItem[]>(savedSelectedFeedback360Statuses, "selectedFeedback360Statuses") ?? [];
    }

    const savedSelectedSelfAssessmentStatuses = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_SELF_ASSESSMENT_STATUSES);
    if (savedSelectedSelfAssessmentStatuses !== null) {
      this.selectedSelfAssessmentStatuses = this.parseWithLogging<MenuItem[]>(savedSelectedSelfAssessmentStatuses, "selectedSelfAssessmentStatuses") ?? [];
    }

    const savedSelectedEvaluationStatuses = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_EVALUATION_STATUSES);
    if (savedSelectedEvaluationStatuses !== null) {
      this.selectedEvaluationStatuses = this.parseWithLogging<MenuItem[]>(savedSelectedEvaluationStatuses, "selectedEvaluationStatuses") ?? [];
    }

    const savedSelectedDeliveryStatuses = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_DELIVERY_STATUSES);
    if (savedSelectedDeliveryStatuses !== null) {
      this.selectedDeliveryStatuses = this.parseWithLogging<MenuItem[]>(savedSelectedDeliveryStatuses, "selectedDeliveryStatuses") ?? [];
    }

    // Ripristina filtri applicati
    const savedAppliedFeedback360Statuses = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_FEEDBACK_360_STATUSES);
    if (savedAppliedFeedback360Statuses !== null) {
      this.appliedFeedback360Statuses = this.parseWithLogging<MenuItem[]>(savedAppliedFeedback360Statuses, "appliedFeedback360Statuses") ?? [];
    }

    const savedAppliedSelfAssessmentStatuses = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_SELF_ASSESSMENT_STATUSES);
    if (savedAppliedSelfAssessmentStatuses !== null) {
      this.appliedSelfAssessmentStatuses = this.parseWithLogging<MenuItem[]>(savedAppliedSelfAssessmentStatuses, "appliedSelfAssessmentStatuses") ?? [];
    }

    const savedAppliedEvaluationStatuses = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_EVALUATION_STATUSES);
    if (savedAppliedEvaluationStatuses !== null) {
      this.appliedEvaluationStatuses = this.parseWithLogging<MenuItem[]>(savedAppliedEvaluationStatuses, "appliedEvaluationStatuses") ?? [];
    }

    const savedAppliedDeliveryStatuses = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_DELIVERY_STATUSES);
    if (savedAppliedDeliveryStatuses !== null) {
      this.appliedDeliveryStatuses = this.parseWithLogging<MenuItem[]>(savedAppliedDeliveryStatuses, "appliedDeliveryStatuses") ?? [];
    }

    // Ripristina filtri avanzati
    const savedSelectedCompanyOptions = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_COMPANY_OPTIONS);
    if (savedSelectedCompanyOptions !== null) {
      this.selectedCompanyOptions = this.parseWithLogging<MenuItem[]>(savedSelectedCompanyOptions, "selectedCompanyOptions") ?? [];
    }
    const savedAppliedCompanyOptions = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_COMPANY_OPTIONS);
    if (savedAppliedCompanyOptions !== null) {
      this.appliedCompanyOptions = this.parseWithLogging<MenuItem[]>(savedAppliedCompanyOptions, "appliedCompanyOptions") ?? [];
    }
    const savedSelectedOfficeOptions = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_OFFICE_OPTIONS);
    if (savedSelectedOfficeOptions !== null) {
      this.selectedOfficeOptions = this.parseWithLogging<MenuItem[]>(savedSelectedOfficeOptions, "selectedOfficeOptions") ?? [];
    }
    const savedAppliedOfficeOptions = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_OFFICE_OPTIONS);
    if (savedAppliedOfficeOptions !== null) {
      this.appliedOfficeOptions = this.parseWithLogging<MenuItem[]>(savedAppliedOfficeOptions, "appliedOfficeOptions") ?? [];
    }
    const savedSelectedDepartmentOptions = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_DEPARTMENT_OPTIONS);
    if (savedSelectedDepartmentOptions !== null) {
      this.selectedDepartmentOptions = this.parseWithLogging<MenuItem[]>(savedSelectedDepartmentOptions, "selectedDepartmentOptions") ?? [];
    }
    const savedAppliedDepartmentOptions = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_DEPARTMENT_OPTIONS);
    if (savedAppliedDepartmentOptions !== null) {
      this.appliedDepartmentOptions = this.parseWithLogging<MenuItem[]>(savedAppliedDepartmentOptions, "appliedDepartmentOptions") ?? [];
    }
    const savedSelectedBUOptions = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_BU_OPTIONS);
    if (savedSelectedBUOptions !== null) {
      this.selectedBUOptions = this.parseWithLogging<MenuItem[]>(savedSelectedBUOptions, "selectedBUOptions") ?? [];
    }
    const savedAppliedBUOptions = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_BU_OPTIONS);
    if (savedAppliedBUOptions !== null) {
      this.appliedBUOptions = this.parseWithLogging<MenuItem[]>(savedAppliedBUOptions, "appliedBUOptions") ?? [];
    }
    const savedSelectedEvaluatorOptions = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_EVALUATOR_OPTIONS);
    if (savedSelectedEvaluatorOptions !== null) {
      this.selectedEvaluatorOptions = this.parseWithLogging<MenuItem[]>(savedSelectedEvaluatorOptions, "selectedEvaluatorOptions") ?? [];
    }
    const savedAppliedEvaluatorsOptions = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_EVALUATORS_OPTIONS);
    if (savedAppliedEvaluatorsOptions !== null) {
      this.appliedEvaluatorsOptions = this.parseWithLogging<MenuItem[]>(savedAppliedEvaluatorsOptions, "appliedEvaluatorsOptions") ?? [];
    }

    // Ripristina ordinamento da ID
    const savedSelectedSortOption = sessionStorage.getItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_SORT_OPTION);
    if (savedSelectedSortOption !== null) {
      this.selectedSortOption = this.parseWithLogging<MenuItem>(savedSelectedSortOption, "selectedSortOption") ?? undefined;
    }

    // Pulisce la sessione subito dopo il ripristino (i dati sono già nello stato del componente)
    if (this.hasDataToRestore) {
      this.clearSessionState();
    }
  }

  /**
   * Pulisce tutte le chiavi di sessione del componente
   */
  private clearSessionState(): void {
    Object.values(AdminMonitoringDetailsComponent.SESSION_KEYS)
      .forEach(key => {
        sessionStorage.removeItem(key);
      });
  }

  /**
   * Gestisce il click su "Conferma e invia"
   */
  async onConfirmAndSend(): Promise<void> {
    if (!this.processId) {
      this.logger.error("Process ID is required for confirm and send");
      return;
    }

    try {
      this.isLoadingConfirmation = true;

      // Recupera gli utenti selezionati che possono confermare e inviare la calibrazione
      const selectedUsers = this.allUsersData
        .filter(user => user.checked && user.canConfirmAndSendCalibration);

      if (selectedUsers.length === 0) {
        this.alertService.showError("Nessun utente selezionato valido per la conferma");
        return;
      }

      // Per ogni utente selezionato, recupera il team completo usando l'endpoint corretto
      // Usiamo il primo utente come riferimento per ottenere il team (come nella calibration)
      const firstSelectedUser = selectedUsers[0];
      this.calibrationUsers = await this.monitoringCalibrationBackofficeApiService
        .getCalibrationMatrixTeam(this.processId, firstSelectedUser.userId);

      // Filtra solo gli utenti selezionati dal team completo
      const selectedUserIds = selectedUsers.map(user => user.userId);
      this.calibrationUsers = this.calibrationUsers.filter(user =>
        selectedUserIds.includes(user.userId));

      // Genera il riepilogo per la modale
      this.confirmationSummary = this.generateConfirmationSummary();

      // Mostra la modale di conferma
      this.showConfirmationModal = true;
    } catch (error) {
      this.logger.error(`Error preparing confirmation modal: ${error?.toString()}`);
      this.alertService.showError("Errore nel preparare la conferma");
    } finally {
      this.isLoadingConfirmation = false;
    }
  }

  async onSendCalibrationCards(user: MonitoringUserSummaryExtended): Promise<void> {
    if (!user) return;
    if (!this.processId) {
      this.logger.error("Process ID is required for send calibration cards");
      return;
    }

    this.isLoadingConfirmation = true;
    try {
      this.logger.info(`Sending calibration cards for user ${user.userId}`);
      this.calibrationUsers = await this.monitoringCalibrationBackofficeApiService.getCalibrationMatrixTeam(this.processId, user.userId);
      // Genera il riepilogo per la modale
      this.confirmationSummary = this.generateConfirmationSummary();
      this.showConfirmationModal = true;
    } catch (error) {
      this.logger.error(`Error sending calibration cards for user ${user.userId}: ${error?.toString()}`);
      this.alertService.showError("Errore durante l'invio delle schede");
    } finally {
      this.isLoadingConfirmation = false;
    }
  }

  /**
   * Gestisce la conferma nella modale
   */
  async onConfirmModal(): Promise<void> {
    if (!this.processId) {
      this.logger.error("Process ID is required for submit calibration");
      return;
    }

    try {
      this.isLoadingConfirmation = true;

      // Filtra solo gli utenti che possono essere modificati
      const modifiableUsers = this.calibrationUsers.filter(user => user.canModifyCalibration);

      if (modifiableUsers.length === 0) {
        this.alertService.showError("Nessun utente modificabile trovato");
        return;
      }

      // Prepara la richiesta di submit
      const submitRequest = {
        processId: this.processId,
        userIds: modifiableUsers.map(user => user.userId)
      };

      // Invia la conferma della calibration
      await this.monitoringCalibrationBackofficeApiService.submitCalibration(submitRequest);

      // Chiudi la modale e mostra successo
      this.showConfirmationModal = false;
      this.alertService.showSuccess("Schede inviate con successo ai manager");

      // Ricarica i dati per aggiornare lo stato
      await this.getUsersDataCount();
    } catch (error) {
      this.logger.error(`Error submitting calibration: ${error?.toString()}`);
      this.alertService.showError("Errore durante l'invio delle schede");
    } finally {
      this.isLoadingConfirmation = false;
    }
  }

  /**
   * Gestisce l'annullamento nella modale
   */
  onCancelModal(): void {
    this.showConfirmationModal = false;
    this.confirmationSummary = [];
    this.calibrationUsers = [];
  }

  /**
   * Gestisce la chiusura della modale
   */
  onCloseModal(): void {
    this.showConfirmationModal = false;
    this.confirmationSummary = [];
    this.calibrationUsers = [];
  }

  /**
   * Genera il riepilogo degli utenti per la modale di conferma
   */
  private generateConfirmationSummary(): PersonSummary[] {
    return this.calibrationUsers
      .filter(user => user.canModifyCalibration)
      .map(user => {
        // Considera spostato solo se:
        // 1. Esiste una posizione di calibration (non è undefined/null)
        // 2. La posizione di calibration è diversa da quella del manager
        const hasBeenMoved = user.calibrationTalentMatrixBox !== undefined
          && user.calibrationTalentMatrixBox !== null
          && user.calibrationTalentMatrixBox !== user.managerTalentMatrixBox;

        return {
          person: user,
          hasBeenMoved,
          currentQuadrant: this.getQuadrantInfo(user)
        };
      });
  }

  /**
   * Ottiene le informazioni del quadrante per un utente
   */
  private getQuadrantInfo(user: TalentMatrixUser): PersonSummary["currentQuadrant"] {
    // Usa la posizione del manager se quella di calibration non è presente
    const positionToUse = !user.calibrationTalentMatrixBox
      ? user.managerTalentMatrixBox
      : user.calibrationTalentMatrixBox;

    // Se non c'è una posizione valida, ritorna UNKNOWN
    if (!positionToUse) {
      return {
        name: "UNKNOWN",
        level: "UNKONWN"
      };
    }

    // Mappa completa dei quadranti basata sui valori dell'API e dell'enum
    const quadrantMap: {
      [key: string]: PersonSummary["currentQuadrant"];
    } = {
      "1_LOW": {
        name: "1 – LOW - Critical Case",
        level: "1 – LOW - Critical Case"
      },
      "2_MID": {
        name: "2 – MID - Effective",
        level: "2 – MID - Effective"
      },
      "3_MID": {
        name: "3 – MID - Expert",
        level: "3 – MID - Expert"
      },
      "4_LOW": {
        name: "4 – LOW - Inconsistent Performer",
        level: "4 – LOW - Inconsistent Performer"
      },
      "5_MID": {
        name: "5 – MID - Core Player",
        level: "5 – MID - Core Player"
      },
      "6_HIGH": {
        name: "6 – HIGH - High Performer",
        level: "6 – HIGH - High Performer"
      },
      "7_MID": {
        name: "7 – MID - Potential Gem",
        level: "7 – MID - Potential Gem"
      },
      "8_HIGH": {
        name: "8 – HIGH - High Potential",
        level: "8 – HIGH - High Potential"
      },
      "9_TOP": {
        name: "9 – TOP - Talent",
        level: "9 – TOP - Talent"
      }
    };

    // Usa la mappa diretta
    return quadrantMap[positionToUse] || { level: positionToUse };
  }

  /**
   * Esporta il report del processo
   */
  async onExportProcessReport(downloadId: string): Promise<void> {
    try {
      if (!this.processId) {
        this.logger.error("Process ID is required for export");
        this.downloadService.completeDownload(downloadId);
        return;
      }

      this.logger.info(`Exporting process report for process: ${this.processId}`);

      await this.monitoringBackofficeApiService.exportProcessReport(this.processId, downloadId);

      this.logger.info("Process report exported successfully");
    } catch (error) {
      this.logger.error(`Error exporting process report: ${String(error)}`);
      this.alertService.showError(this.translate.instant("generic.ERROR"));
    } finally {
      // Completa il download passando l'ID per evitare di resettare altri download in corso
      this.downloadService.completeDownload(downloadId);
    }
  }
}

export type UsersCountParams = {
  searchedText: string | null;
  selfAssessmentStatus?: string[] | undefined;
  evaluationStatus?: string[] | undefined;
  feedback360Status?: string[] | undefined;
  developmentPlanStatus?: string[] | undefined;
  processId: string;
  deliveryStatus?: string[] | undefined;
  company?: string[] | undefined;
  evaluators?: string[] | undefined;
  department?: string[] | undefined;
  office?: string[] | undefined;
  BU?: string[] | undefined;
};

export type UsersDataParams = {
  searchedText: string | null;
  selfAssessmentStatus?: string[] | undefined;
  evaluationStatus?: string[] | undefined;
  feedback360Status?: string[] | null;
  developmentPlanStatus?: string[] | null;
  processId: string;
  deliveryStatus?: string[] | undefined;
  company?: string[] | undefined;
  evaluators?: string[] | undefined;
  department?: string[] | undefined;
  office?: string[] | undefined;
  BU?: string[] | undefined;
  fromRecord?: number;
  numRecords?: number;
  sortBy?: string | null;
};

export type AllUsersDataForCheckedParams = {
  searchedText: string | null;
  selfAssessmentStatus?: string[] | undefined;
  evaluationStatus?: string[] | undefined;
  feedback360Status?: string[] | null;
  developmentPlanStatus?: string[] | null;
  processId: string;
  deliveryStatus?: string[] | undefined;
  office?: string[] | undefined;
  BU?: string[] | undefined;
  department?: string[] | undefined;
  company?: string[] | undefined;
  evaluators?: string[] | undefined;
};
